import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import FuelHistoryTab from '../components/FuelHistoryTab';
import ProductImagesManager from '../components/ProductImagesManager';
import {
  Building2, Package, Users, TrendingUp, LogOut,
  Plus, Edit, Trash2, RefreshCw, Search, Download,
  MapPin, Phone, Mail, User, DollarSign, ShoppingCart,
  AlertTriangle, CheckCircle, Clock, BarChart3, PieChart,
  ChevronDown, ChevronUp, Eye, Key, Shield, Store,
  Loader2, Calendar, Filter, X, Save, ArrowLeft, Truck,
  FileCheck, CreditCard, IdCard, Star, Fuel
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => localStorage.getItem('halubri_super_admin_token');

const api = () => axios.create({
  baseURL: API,
  headers: { Authorization: `Bearer ${getToken()}` }
});

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [stations, setStations] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [salesByStation, setSalesByStation] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [stockOverview, setStockOverview] = useState(null);
  const [customerAnalytics, setCustomerAnalytics] = useState(null);
  
  // UI states
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [showStationModal, setShowStationModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [adminRes, overviewRes] = await Promise.all([
        api().get('/super-admin/auth/me'),
        api().get('/super-admin/dashboard/overview')
      ]);
      setAdmin(adminRes.data);
      setOverview(overviewRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('halubri_super_admin_token');
        navigate('/admin/login');
      }
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate, loadData]);

  const loadStations = useCallback(async () => {
    try {
      const res = await api().get('/super-admin/stations');
      setStations(res.data);
    } catch (err) {
      toast.error('Erro ao carregar estações');
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const res = await api().get('/super-admin/products');
      setProducts(res.data);
    } catch (err) {
      toast.error('Erro ao carregar produtos');
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const query = userTypeFilter !== 'all' ? `?client_type=${userTypeFilter}` : '';
      const res = await api().get(`/super-admin/users${query}`);
      setUsers(res.data);
    } catch (err) {
      toast.error('Erro ao carregar utilizadores');
    }
  }, [userTypeFilter]);

  const loadSalesData = useCallback(async () => {
    try {
      const [byStationRes, trendRes, analyticsRes] = await Promise.all([
        api().get(`/super-admin/dashboard/sales-by-station?period=${selectedPeriod}`),
        api().get(`/super-admin/dashboard/sales-trend?period=${selectedPeriod}`),
        api().get('/super-admin/dashboard/customer-analytics')
      ]);
      setSalesByStation(byStationRes.data);
      setSalesTrend(trendRes.data);
      setCustomerAnalytics(analyticsRes.data);
    } catch (err) {
      toast.error('Erro ao carregar dados de vendas');
    }
  }, [selectedPeriod]);

  const loadStockOverview = useCallback(async () => {
    try {
      const res = await api().get('/super-admin/stock/overview');
      setStockOverview(res.data);
    } catch (err) {
      toast.error('Erro ao carregar stock');
    }
  }, []);

  const loadDrivers = useCallback(async () => {
    try {
      const [allDriversRes, pendingRes] = await Promise.all([
        api().get('/super-admin/drivers'),
        api().get('/super-admin/drivers/pending')
      ]);
      setDrivers(allDriversRes.data);
      setPendingDrivers(pendingRes.data);
    } catch (err) {
      toast.error('Erro ao carregar entregadores');
    }
  }, []);

  const verifyDriver = async (driverId) => {
    try {
      await api().post(`/super-admin/drivers/${driverId}/verify`);
      toast.success('Entregador verificado');
      loadDrivers();
    } catch (err) {
      toast.error('Erro ao verificar entregador');
    }
  };

  const rejectDriver = async (driverId) => {
    try {
      await api().post(`/super-admin/drivers/${driverId}/reject`);
      toast.success('Registo rejeitado');
      loadDrivers();
    } catch (err) {
      toast.error('Erro ao rejeitar entregador');
    }
  };

  useEffect(() => {
    if (activeTab === 'stations') loadStations();
    if (activeTab === 'products') loadProducts();
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'drivers') loadDrivers();
    if (activeTab === 'sales') loadSalesData();
    if (activeTab === 'stock') loadStockOverview();
  }, [activeTab, loadStations, loadProducts, loadUsers, loadDrivers, loadSalesData, loadStockOverview]);

  const handleLogout = () => {
    localStorage.removeItem('halubri_super_admin_token');
    navigate('/admin/login');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-AO', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value) + ' AOA';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-AO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Reset user password
  const handleResetPassword = async (userId, newPassword) => {
    try {
      await api().post(`/super-admin/users/${userId}/reset-password?new_password=${newPassword}`);
      toast.success('Palavra-passe alterada com sucesso');
      setSelectedUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar palavra-passe');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-sonangol-orange animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">A carregar painel de administração...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="bg-gradient-to-r from-zinc-900 to-zinc-800 border-b border-zinc-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-sonangol-orange/20 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-sonangol-orange" />
              </div>
              <div>
                <h1 className="font-barlow font-bold text-xl text-white">SUPER ADMIN</h1>
                <p className="text-zinc-400 text-sm">HáLubri · Sonangol</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{admin?.name}</p>
                <p className="text-zinc-400 text-sm">{admin?.admin_id}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadData}
                className="text-zinc-400 hover:text-white"
              >
                <RefreshCw className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-zinc-400 hover:text-white"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Dashboard', icon: BarChart3 },
            { id: 'stations', label: 'Estações', icon: Building2 },
            { id: 'products', label: 'Produtos', icon: Package },
            { id: 'users', label: 'Clientes', icon: Users },
            { id: 'drivers', label: 'Entregadores', icon: Truck },
            { id: 'sales', label: 'Vendas', icon: TrendingUp },
            { id: 'stock', label: 'Stock', icon: Store },
            { id: 'fuel', label: 'Combustível', icon: Fuel }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              className={activeTab === tab.id 
                ? "bg-sonangol-orange hover:bg-orange-600 text-white" 
                : "border-zinc-600 text-white bg-zinc-800 hover:bg-zinc-700"
              }
              onClick={() => setActiveTab(tab.id)}
              data-testid={`super-admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && overview && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <StatCard 
                icon={Building2} 
                label="Estações" 
                value={overview.stations} 
                color="orange"
              />
              <StatCard 
                icon={Users} 
                label="Clientes" 
                value={overview.customers.total} 
                subtitle={`${overview.customers.individual} Part. | ${overview.customers.corporate} Corp.`}
                color="blue"
              />
              <StatCard 
                icon={User} 
                label="Funcionários" 
                value={overview.staff} 
                color="green"
              />
              <StatCard 
                icon={Package} 
                label="Produtos" 
                value={overview.products} 
                color="purple"
              />
              <StatCard 
                icon={ShoppingCart} 
                label="Pedidos Hoje" 
                value={overview.orders.today} 
                subtitle={`${overview.orders.pending} pendentes`}
                color="yellow"
              />
              <StatCard 
                icon={AlertTriangle} 
                label="Alertas Stock" 
                value={overview.alerts.low_stock} 
                color="red"
              />
            </div>

            {/* Revenue Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-700/50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="text-green-400 font-medium">Receita Total</span>
                </div>
                <p className="text-3xl font-barlow font-bold text-white">
                  {formatCurrency(overview.revenue.total)}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-700/50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-blue-400 font-medium">Receita do Mês</span>
                </div>
                <p className="text-3xl font-barlow font-bold text-white">
                  {formatCurrency(overview.revenue.month)}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 border border-yellow-700/50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-yellow-600/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-yellow-400" />
                  </div>
                  <span className="text-yellow-400 font-medium">Receita Hoje</span>
                </div>
                <p className="text-3xl font-barlow font-bold text-white">
                  {formatCurrency(overview.revenue.today)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stations Tab */}
        {activeTab === 'stations' && (
          <StationsTab 
            stations={stations}
            onRefresh={loadStations}
            api={api}
            formatCurrency={formatCurrency}
          />
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <ProductsTab 
            products={products}
            onRefresh={loadProducts}
            api={api}
            formatCurrency={formatCurrency}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <UsersTab 
            users={users}
            userTypeFilter={userTypeFilter}
            setUserTypeFilter={setUserTypeFilter}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onRefresh={loadUsers}
            api={api}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            onResetPassword={handleResetPassword}
          />
        )}

        {/* Drivers Tab */}
        {activeTab === 'drivers' && (
          <DriversTab 
            drivers={drivers}
            pendingDrivers={pendingDrivers}
            selectedDriver={selectedDriver}
            setSelectedDriver={setSelectedDriver}
            onVerify={verifyDriver}
            onReject={rejectDriver}
            onRefresh={loadDrivers}
            api={api}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        )}

        {/* Sales Tab */}
        {activeTab === 'sales' && (
          <SalesTab 
            salesByStation={salesByStation}
            salesTrend={salesTrend}
            customerAnalytics={customerAnalytics}
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={setSelectedPeriod}
            formatCurrency={formatCurrency}
          />
        )}

        {activeTab === 'fuel' && <FuelHistoryTab api={api} />}

        {/* Stock Tab */}
        {activeTab === 'stock' && stockOverview && (
          <StockTab 
            stockOverview={stockOverview}
            api={api}
            onRefresh={loadStockOverview}
          />
        )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subtitle, color }) => {
  const colors = {
    orange: 'from-orange-900/30 to-orange-800/20 border-orange-700/50 text-orange-400',
    blue: 'from-blue-900/30 to-blue-800/20 border-blue-700/50 text-blue-400',
    green: 'from-green-900/30 to-green-800/20 border-green-700/50 text-green-400',
    purple: 'from-purple-900/30 to-purple-800/20 border-purple-700/50 text-purple-400',
    yellow: 'from-yellow-900/30 to-yellow-800/20 border-yellow-700/50 text-yellow-400',
    red: 'from-red-900/30 to-red-800/20 border-red-700/50 text-red-400'
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-2xl font-barlow font-bold text-white">{value}</p>
      {subtitle && <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>}
    </div>
  );
};

// Stations Tab Component
const StationsTab = ({ stations, onRefresh, api, formatCurrency }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [formData, setFormData] = useState({
    name: '', address: '', city: '', province: '',
    phone: '', email: '', manager_name: '', operating_hours: '06:00 - 22:00'
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingStation) {
        await api().put(`/super-admin/stations/${editingStation.id}`, formData);
        toast.success('Estação actualizada!');
      } else {
        await api().post('/super-admin/stations', formData);
        toast.success('Estação criada!');
      }
      setShowModal(false);
      setEditingStation(null);
      setFormData({ name: '', address: '', city: '', province: '', phone: '', email: '', manager_name: '', operating_hours: '06:00 - 22:00' });
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao guardar estação');
    }
    setSaving(false);
  };

  const handleDelete = async (stationId) => {
    setDeleting(stationId);
    try {
      await api().delete(`/super-admin/stations/${stationId}`);
      toast.success('Estação desactivada com sucesso!');
      setShowDeleteConfirm(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao desactivar estação');
    }
    setDeleting(null);
  };

  const openEdit = (station) => {
    setEditingStation(station);
    setFormData({
      name: station.name || '',
      address: station.address || '',
      city: station.city || '',
      province: station.province || '',
      phone: station.phone || '',
      email: station.email || '',
      manager_name: station.manager_name || '',
      operating_hours: station.operating_hours || '06:00 - 22:00'
    });
    setShowModal(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-barlow font-bold text-white">Gestão de Estações</h2>
        <Button onClick={() => { setEditingStation(null); setFormData({ name: '', address: '', city: '', province: '', phone: '', email: '', manager_name: '', operating_hours: '06:00 - 22:00' }); setShowModal(true); }} className="bg-sonangol-orange hover:bg-orange-600" data-testid="add-station-btn">
          <Plus className="w-4 h-4 mr-2" /> Nova Estação
        </Button>
      </div>

      <div className="grid gap-4">
        {stations.map(station => (
          <div key={station.id} className="bg-zinc-900 border border-zinc-700 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-white">{station.name}</h3>
                  <Badge className={station.is_active !== false ? "bg-green-600/20 text-green-400" : "bg-red-600/20 text-red-400"}>
                    {station.is_active !== false ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
                <p className="text-zinc-400 text-sm flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {station.address}, {station.city}
                </p>
                {station.phone && (
                  <p className="text-zinc-400 text-sm flex items-center gap-1 mt-1">
                    <Phone className="w-3 h-3" /> {station.phone}
                  </p>
                )}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(station)} className="text-zinc-300 hover:text-white hover:bg-zinc-700" data-testid={`edit-station-${station.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                {station.is_active !== false && (
                  <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(station)} className="text-red-400 hover:text-red-300 hover:bg-red-900/20" data-testid={`delete-station-${station.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-zinc-700">
              <div>
                <p className="text-zinc-500 text-xs">Vendas Total</p>
                <p className="text-white font-bold">{formatCurrency(station.total_sales || 0)}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-xs">Pedidos</p>
                <p className="text-white font-bold">{station.order_count || 0}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-xs">Funcionários</p>
                <p className="text-white font-bold">{station.staff_count || 0}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-xs">Alertas Stock</p>
                <p className={`font-bold ${station.low_stock_alerts > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {station.low_stock_alerts || 0}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-700">
              <h3 className="text-xl font-bold text-white">
                {editingStation ? 'Editar Estação' : 'Nova Estação'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Nome *</Label>
                  <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
                </div>
                <div>
                  <Label className="text-zinc-400">Cidade *</Label>
                  <Input value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">Endereço *</Label>
                <Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Província *</Label>
                  <Input value={formData.province} onChange={e => setFormData({...formData, province: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
                </div>
                <div>
                  <Label className="text-zinc-400">Horário</Label>
                  <Input value={formData.operating_hours} onChange={e => setFormData({...formData, operating_hours: e.target.value})} className="bg-zinc-800 border-zinc-700" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Telefone</Label>
                  <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="bg-zinc-800 border-zinc-700" />
                </div>
                <div>
                  <Label className="text-zinc-400">Email</Label>
                  <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="bg-zinc-800 border-zinc-700" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">Gestor</Label>
                <Input value={formData.manager_name} onChange={e => setFormData({...formData, manager_name: e.target.value})} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1 bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700">Cancelar</Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-sonangol-orange hover:bg-orange-600 text-white" data-testid="save-station-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Desactivar Estação</h3>
                <p className="text-zinc-400 text-sm">Esta acção pode ser revertida</p>
              </div>
            </div>
            <p className="text-zinc-300 mb-6">
              Tem certeza que deseja desactivar a estação <strong className="text-white">{showDeleteConfirm.name}</strong>? 
              A estação não estará mais disponível para novos pedidos.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)} className="flex-1 bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700">
                Cancelar
              </Button>
              <Button 
                onClick={() => handleDelete(showDeleteConfirm.id)} 
                disabled={deleting === showDeleteConfirm.id} 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-station-btn"
              >
                {deleting === showDeleteConfirm.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Desactivar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Products Tab Component
const ProductsTab = ({ products, onRefresh, api, formatCurrency }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', category: 'super', viscosity: '5W30',
    base_type: 'synthetic', volume_liters: 1, price_aoa: 0, images: []
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const categories = [
    { value: 'super', label: 'Super' },
    { value: 'extra', label: 'Extra' },
    { value: 'classic', label: 'Classic' },
    { value: 'diesel_turbo', label: 'Diesel Turbo' },
    { value: 'moto', label: 'Moto' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingProduct) {
        await api().put(`/super-admin/products/${editingProduct.id}`, formData);
        toast.success('Produto actualizado!');
      } else {
        await api().post('/super-admin/products', formData);
        toast.success('Produto criado!');
      }
      setShowModal(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao guardar produto');
    }
    setSaving(false);
  };

  const handleDelete = async (productId) => {
    setDeleting(productId);
    try {
      await api().delete(`/super-admin/products/${productId}`);
      toast.success('Produto desactivado com sucesso!');
      setShowDeleteConfirm(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao desactivar produto');
    }
    setDeleting(null);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      category: product.category || 'super',
      viscosity: product.viscosity || '5W30',
      base_type: product.base_type || 'synthetic',
      volume_liters: product.volume_liters || 1,
      price_aoa: product.price_aoa || 0,
      images: product.images?.length ? product.images : (product.image_url ? [product.image_url] : [])
    });
    setShowModal(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-barlow font-bold text-white">Gestão de Produtos</h2>
        <Button onClick={() => { setEditingProduct(null); setFormData({ name: '', description: '', category: 'super', viscosity: '5W30', base_type: 'synthetic', volume_liters: 1, price_aoa: 0, images: [] }); setShowModal(true); }} className="bg-sonangol-orange hover:bg-orange-600" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Produto
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map(product => (
          <div key={product.id} className="bg-zinc-900 border border-zinc-700 rounded-xl overflow-hidden">
            <div className="aspect-video bg-zinc-800 flex items-center justify-center">
              {product.image_url ? (
                <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <Package className="w-12 h-12 text-zinc-600" />
              )}
            </div>
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-bold text-white">{product.name}</h3>
                  <p className="text-zinc-400 text-sm">{product.viscosity} · {product.volume_liters}L</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(product)} className="text-zinc-300 hover:text-white hover:bg-zinc-700" data-testid={`edit-product-${product.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  {product.is_active !== false && (
                    <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(product)} className="text-red-400 hover:text-red-300 hover:bg-red-900/20" data-testid={`delete-product-${product.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <Badge className="bg-zinc-700 text-zinc-300 mb-3">{product.category}</Badge>
              <div className="flex justify-between items-center pt-3 border-t border-zinc-700">
                <p className="text-sonangol-orange font-bold">{formatCurrency(product.price_aoa)}</p>
                <p className="text-zinc-400 text-sm">Stock: {product.total_stock || 0}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-700">
              <h3 className="text-xl font-bold text-white">
                {editingProduct ? 'Editar Produto' : 'Novo Produto'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Label className="text-zinc-400">Nome *</Label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Descrição *</Label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} required className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Categoria *</Label>
                  <select value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white">
                    {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-400">Viscosidade *</Label>
                  <Input value={formData.viscosity} onChange={e => setFormData({...formData, viscosity: e.target.value})} required className="bg-zinc-800 border-zinc-700" placeholder="Ex: 5W30" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-zinc-400">Tipo Base *</Label>
                  <select value={formData.base_type} onChange={e => setFormData({...formData, base_type: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white">
                    <option value="synthetic">Sintético</option>
                    <option value="semi_synthetic">Semi-Sintético</option>
                    <option value="mineral">Mineral</option>
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-400">Volume (L) *</Label>
                  <Input type="number" step="0.5" value={formData.volume_liters} onChange={e => setFormData({...formData, volume_liters: parseFloat(e.target.value)})} required className="bg-zinc-800 border-zinc-700" />
                </div>
                <div>
                  <Label className="text-zinc-400">Preço (AOA) *</Label>
                  <Input type="number" value={formData.price_aoa} onChange={e => setFormData({...formData, price_aoa: parseFloat(e.target.value)})} required className="bg-zinc-800 border-zinc-700" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">Imagens do Produto</Label>
                <ProductImagesManager images={formData.images} onChange={images => setFormData(f => ({ ...f, images }))} api={api} />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1 bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700">Cancelar</Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-sonangol-orange hover:bg-orange-600 text-white" data-testid="save-product-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Desactivar Produto</h3>
                <p className="text-zinc-400 text-sm">Esta acção pode ser revertida</p>
              </div>
            </div>
            <p className="text-zinc-300 mb-6">
              Tem certeza que deseja desactivar o produto <strong className="text-white">{showDeleteConfirm.name}</strong>? 
              O produto não estará mais disponível para venda.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)} className="flex-1 bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700">
                Cancelar
              </Button>
              <Button 
                onClick={() => handleDelete(showDeleteConfirm.id)} 
                disabled={deleting === showDeleteConfirm.id} 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-product-btn"
              >
                {deleting === showDeleteConfirm.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Desactivar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Users Tab Component
const UsersTab = ({ users, userTypeFilter, setUserTypeFilter, searchTerm, setSearchTerm, onRefresh, api, formatCurrency, formatDate, onResetPassword }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [loadingDetails, setLoadingDetails] = useState(false);

  const loadUserDetails = async (userId) => {
    setLoadingDetails(true);
    try {
      const res = await api().get(`/super-admin/users/${userId}`);
      setUserDetails(res.data);
    } catch (err) {
      toast.error('Erro ao carregar detalhes');
    }
    setLoadingDetails(false);
  };

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return user.name?.toLowerCase().includes(search) || 
           user.email?.toLowerCase().includes(search) ||
           user.company_name?.toLowerCase().includes(search);
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <h2 className="text-xl font-barlow font-bold text-white">Gestão de Clientes</h2>
        <div className="flex gap-2">
          <Button variant={userTypeFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setUserTypeFilter('all')} className={userTypeFilter === 'all' ? 'bg-sonangol-orange text-white' : 'bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700'}>Todos</Button>
          <Button variant={userTypeFilter === 'individual' ? 'default' : 'outline'} size="sm" onClick={() => setUserTypeFilter('individual')} className={userTypeFilter === 'individual' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700'}>Particulares</Button>
          <Button variant={userTypeFilter === 'corporate' ? 'default' : 'outline'} size="sm" onClick={() => setUserTypeFilter('corporate')} className={userTypeFilter === 'corporate' ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700'}>Empresas</Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input placeholder="Pesquisar por nome, email ou empresa..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-zinc-800 border-zinc-700" />
      </div>

      <div className="bg-zinc-900 border border-zinc-700 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left text-zinc-400 text-xs font-medium p-4">Cliente</th>
              <th className="text-left text-zinc-400 text-xs font-medium p-4">Tipo</th>
              <th className="text-left text-zinc-400 text-xs font-medium p-4 hidden md:table-cell">Pedidos</th>
              <th className="text-left text-zinc-400 text-xs font-medium p-4 hidden md:table-cell">Total Gasto</th>
              <th className="text-left text-zinc-400 text-xs font-medium p-4">Acções</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id} className="border-t border-zinc-700 hover:bg-zinc-800/50">
                <td className="p-4">
                  <div>
                    <p className="text-white font-medium">{user.name}</p>
                    <p className="text-zinc-400 text-sm">{user.email}</p>
                    {user.company_name && <p className="text-purple-400 text-xs">{user.company_name}</p>}
                  </div>
                </td>
                <td className="p-4">
                  <Badge className={user.client_type === 'corporate' ? 'bg-purple-600/20 text-purple-400' : 'bg-blue-600/20 text-blue-400'}>
                    {user.client_type === 'corporate' ? 'Empresa' : 'Particular'}
                  </Badge>
                </td>
                <td className="p-4 hidden md:table-cell text-white">{user.order_count || 0}</td>
                <td className="p-4 hidden md:table-cell text-white">{formatCurrency(user.total_spent || 0)}</td>
                <td className="p-4">
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(user); loadUserDetails(user.id); }} className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(user); setNewPassword(''); }} className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20">
                      <Key className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Detalhes do Cliente</h3>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(null); setUserDetails(null); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {loadingDetails ? (
              <div className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-zinc-400" /></div>
            ) : userDetails ? (
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-400 text-sm">Nome</p>
                    <p className="text-white font-medium">{userDetails.user.name}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Email</p>
                    <p className="text-white">{userDetails.user.email}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Telefone</p>
                    <p className="text-white">{userDetails.user.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Tipo</p>
                    <Badge className={userDetails.user.client_type === 'corporate' ? 'bg-purple-600' : 'bg-blue-600'}>
                      {userDetails.user.client_type === 'corporate' ? 'Empresa' : 'Particular'}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 p-4 bg-zinc-800 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{userDetails.stats.total_orders}</p>
                    <p className="text-zinc-400 text-sm">Pedidos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(userDetails.stats.total_spent)}</p>
                    <p className="text-zinc-400 text-sm">Total Gasto</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-400">{formatCurrency(userDetails.stats.avg_order_value)}</p>
                    <p className="text-zinc-400 text-sm">Média/Pedido</p>
                  </div>
                </div>

                {userDetails.favorite_products?.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-2">Produtos Favoritos</h4>
                    <div className="space-y-2">
                      {userDetails.favorite_products.map(p => (
                        <div key={p._id} className="flex justify-between bg-zinc-800 p-2 rounded">
                          <span className="text-zinc-300">{p.name}</span>
                          <span className="text-zinc-400">{p.count} unid.</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Password Reset */}
                <div className="border-t border-zinc-700 pt-4">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Key className="w-4 h-4" /> Redefinir Palavra-passe
                  </h4>
                  <div className="flex gap-2">
                    <Input type="password" placeholder="Nova palavra-passe" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="bg-zinc-800 border-zinc-700" />
                    <Button onClick={() => onResetPassword(selectedUser.id, newPassword)} disabled={newPassword.length < 6} className="bg-sonangol-orange hover:bg-orange-600">
                      Alterar
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6">
                <div className="border-t border-zinc-700 pt-4">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Key className="w-4 h-4" /> Redefinir Palavra-passe
                  </h4>
                  <div className="flex gap-2">
                    <Input type="password" placeholder="Nova palavra-passe (min. 6 caracteres)" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="bg-zinc-800 border-zinc-700" />
                    <Button onClick={() => onResetPassword(selectedUser.id, newPassword)} disabled={newPassword.length < 6} className="bg-sonangol-orange hover:bg-orange-600">
                      Alterar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Sales Tab Component
const SalesTab = ({ salesByStation, salesTrend, customerAnalytics, selectedPeriod, setSelectedPeriod, formatCurrency }) => {
  const periods = [
    { value: 'today', label: 'Hoje' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mês' },
    { value: 'quarter', label: 'Trimestre' },
    { value: 'semester', label: 'Semestre' },
    { value: 'year', label: 'Ano' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-barlow font-bold text-white">Relatórios de Vendas</h2>
        <div className="flex gap-2 flex-wrap">
          {periods.map(p => (
            <Button key={p.value} variant={selectedPeriod === p.value ? 'default' : 'outline'} size="sm" onClick={() => setSelectedPeriod(p.value)} className={selectedPeriod === p.value ? 'bg-sonangol-orange text-white' : 'bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700'}>
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Sales by Station */}
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Vendas por Estação</h3>
        <div className="space-y-3">
          {salesByStation.map((station, i) => (
            <div key={station._id || i} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-sonangol-orange/20 rounded-full flex items-center justify-center text-sonangol-orange font-bold">
                  {i + 1}
                </div>
                <div>
                  <p className="text-white font-medium">{station.station_name || 'Estação'}</p>
                  <p className="text-zinc-400 text-sm">{station.order_count} pedidos</p>
                </div>
              </div>
              <p className="text-green-400 font-bold">{formatCurrency(station.total_sales)}</p>
            </div>
          ))}
          {salesByStation.length === 0 && (
            <p className="text-zinc-400 text-center py-4">Sem dados para o período seleccionado</p>
          )}
        </div>
      </div>

      {/* Customer Analytics */}
      {customerAnalytics && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Análise de Clientes</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-zinc-400">Novos Clientes (30 dias)</span>
                <span className="text-white font-bold">{customerAnalytics.new_customers_month}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Clientes Activos (30 dias)</span>
                <span className="text-white font-bold">{customerAnalytics.active_customers_month}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Valor Médio/Pedido</span>
                <span className="text-green-400 font-bold">{formatCurrency(customerAnalytics.avg_order_value)}</span>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Método de Entrega</h3>
            <div className="space-y-4">
              {Object.entries(customerAnalytics.delivery_method_split || {}).map(([method, count]) => (
                <div key={method} className="flex justify-between items-center">
                  <span className="text-zinc-400">{method === 'pickup' ? 'Recolha' : 'Entrega'}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden">
                      <div className={`h-full ${method === 'pickup' ? 'bg-blue-500' : 'bg-green-500'}`} style={{ width: `${(count / Object.values(customerAnalytics.delivery_method_split).reduce((a, b) => a + b, 0)) * 100}%` }} />
                    </div>
                    <span className="text-white font-bold">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stock Tab Component
const StockTab = ({ stockOverview, api, onRefresh }) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-barlow font-bold text-white">Visão Global do Stock</h2>
        <Button onClick={onRefresh} variant="outline" className="bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700"><RefreshCw className="w-4 h-4 mr-2" /> Actualizar</Button>
      </div>

      {/* Critical Alerts */}
      {stockOverview.critical_alerts?.length > 0 && (
        <div className="bg-red-900/20 border border-red-700 rounded-xl p-4">
          <h3 className="text-red-400 font-bold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Alertas Críticos (Stock &lt; 5)
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stockOverview.critical_alerts.map((alert) => (
              <div key={`${alert.station_id}-${alert.product_id}`} className="bg-red-900/30 p-3 rounded-lg">
                <p className="text-white font-medium">{alert.product_name}</p>
                <p className="text-red-400 text-sm">Quantidade: {alert.quantity}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stock by Product */}
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Stock por Produto</h3>
        <div className="space-y-3">
          {stockOverview.by_product?.map(product => (
            <div key={product._id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
              <div>
                <p className="text-white font-medium">{product.product_name}</p>
                <p className="text-zinc-400 text-sm">{product.stations_count} estações</p>
              </div>
              <div className="text-right">
                <p className={`font-bold ${product.total_quantity < 20 ? 'text-red-400' : product.total_quantity < 50 ? 'text-yellow-400' : 'text-green-400'}`}>
                  {product.total_quantity} unid.
                </p>
                {product.low_stock_stations > 0 && (
                  <p className="text-red-400 text-xs">{product.low_stock_stations} est. c/ stock baixo</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stock by Station */}
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Stock por Estação</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stockOverview.by_station?.map(station => (
            <div key={station._id} className="bg-zinc-800 rounded-lg p-4">
              <h4 className="text-white font-medium mb-2">{station.station_name}</h4>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Total</span>
                <span className="text-white">{station.total_quantity} unid.</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Produtos</span>
                <span className="text-white">{station.total_products}</span>
              </div>
              {station.low_stock_count > 0 && (
                <div className="flex justify-between text-sm mt-2 pt-2 border-t border-zinc-700">
                  <span className="text-red-400">Stock Baixo</span>
                  <span className="text-red-400">{station.low_stock_count}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Drivers Tab Component
const DriversTab = ({ drivers, pendingDrivers, selectedDriver, setSelectedDriver, onVerify, onReject, onRefresh, api, formatCurrency, formatDate }) => {
  const [viewingDriver, setViewingDriver] = useState(null);
  const [driverDetails, setDriverDetails] = useState(null);
  const recentDeliveries = useMemo(() => driverDetails?.delivery_history?.slice(0, 20) || [], [driverDetails]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const loadDriverDetails = async (driverId) => {
    setLoadingDetails(true);
    try {
      const res = await api().get(`/super-admin/drivers/${driverId}`);
      setDriverDetails(res.data);
      setViewingDriver(driverId);
    } catch (err) {
      toast.error('Erro ao carregar detalhes');
    }
    setLoadingDetails(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-barlow font-bold text-white">Gestão de Entregadores</h2>
        <Button onClick={onRefresh} variant="outline" className="bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>

      {/* Pending Approvals */}
      {pendingDrivers.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-xl p-4">
          <h3 className="text-yellow-400 font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" /> Aguardando Verificação ({pendingDrivers.length})
          </h3>
          <div className="grid gap-4">
            {pendingDrivers.map(driver => (
              <div key={driver.id} className="bg-zinc-900 border border-zinc-700 rounded-xl p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-zinc-800 rounded-full flex items-center justify-center">
                      <User className="w-7 h-7 text-zinc-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold">{driver.name}</h4>
                      <p className="text-zinc-400 text-sm">{driver.driver_id} • {driver.email}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{driver.driver_type === 'individual' ? 'Individual' : 'Corporate'}</Badge>
                        <Badge variant="outline" className="text-xs">{driver.vehicle_type}</Badge>
                      </div>
                    </div>
                  </div>
                  
                  {/* Document Links */}
                  <div className="flex flex-wrap gap-2">
                    {driver.id_card_photo && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${driver.id_card_photo}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1 bg-zinc-800 rounded-lg text-zinc-300 text-sm hover:bg-zinc-700">
                        <IdCard className="w-4 h-4" /> Ver BI
                      </a>
                    )}
                    {driver.driver_license_photo && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${driver.driver_license_photo}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1 bg-zinc-800 rounded-lg text-zinc-300 text-sm hover:bg-zinc-700">
                        <CreditCard className="w-4 h-4" /> Ver Carta
                      </a>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button onClick={() => onVerify(driver.id)} className="bg-green-600 hover:bg-green-700 text-white">
                      <CheckCircle className="w-4 h-4 mr-1" /> Verificar
                    </Button>
                    <Button onClick={() => onReject(driver.id)} variant="outline" className="border-red-600 text-red-400 hover:bg-red-900/20">
                      <X className="w-4 h-4 mr-1" /> Rejeitar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verified Drivers List */}
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Truck className="w-5 h-5 text-green-500" /> Entregadores Verificados ({drivers.filter(d => d.is_verified).length})
        </h3>
        
        <div className="grid gap-4">
          {drivers.filter(d => d.is_verified).map(driver => (
            <div key={driver.id} className="bg-zinc-800 rounded-xl p-4 hover:bg-zinc-750 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  {driver.profile_photo ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${driver.profile_photo}`} alt={driver.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 bg-zinc-700 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-zinc-400" />
                    </div>
                  )}
                  <div>
                    <h4 className="text-white font-bold">{driver.name}</h4>
                    <p className="text-zinc-400 text-sm">{driver.driver_id}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{driver.total_deliveries || 0}</p>
                    <p className="text-zinc-500 text-xs">Entregas</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(driver.total_earnings || 0)}</p>
                    <p className="text-zinc-500 text-xs">Ganhos</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-white font-bold">{(driver.rating || 5).toFixed(1)}</span>
                    </div>
                    <p className="text-zinc-500 text-xs">Rating</p>
                  </div>
                  <Badge className={`${driver.status === 'available' ? 'bg-green-600' : driver.status === 'busy' ? 'bg-yellow-600' : 'bg-zinc-600'}`}>
                    {driver.status === 'available' ? 'Disponível' : driver.status === 'busy' ? 'Ocupado' : 'Offline'}
                  </Badge>
                </div>
                
                <Button onClick={() => loadDriverDetails(driver.id)} variant="outline" className="border-zinc-600 text-white hover:bg-zinc-700">
                  <Eye className="w-4 h-4 mr-1" /> Detalhes
                </Button>
              </div>
            </div>
          ))}
          
          {drivers.filter(d => d.is_verified).length === 0 && (
            <div className="text-center py-8 text-zinc-500">
              Nenhum entregador verificado
            </div>
          )}
        </div>
      </div>

      {/* Driver Details Modal */}
      {viewingDriver && driverDetails && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-zinc-900 p-6 border-b border-zinc-700 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Detalhes do Entregador</h3>
              <Button variant="ghost" onClick={() => { setViewingDriver(null); setDriverDetails(null); }} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Driver Info */}
              <div className="flex items-center gap-4">
                {driverDetails.driver?.profile_photo ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${driverDetails.driver.profile_photo}`} alt={driverDetails.driver.name} className="w-20 h-20 rounded-full object-cover" />
                ) : (
                  <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center">
                    <User className="w-10 h-10 text-zinc-400" />
                  </div>
                )}
                <div>
                  <h4 className="text-2xl font-bold text-white">{driverDetails.driver?.name}</h4>
                  <p className="text-zinc-400">{driverDetails.driver?.driver_id} • {driverDetails.driver?.email}</p>
                  <p className="text-zinc-400">{driverDetails.driver?.phone}</p>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-white">{driverDetails.stats?.total_deliveries || 0}</p>
                  <p className="text-zinc-400 text-sm">Total Entregas</p>
                </div>
                <div className="bg-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">{formatCurrency(driverDetails.stats?.total_earnings || 0)}</p>
                  <p className="text-zinc-400 text-sm">Total Ganhos</p>
                </div>
                <div className="bg-zinc-800 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                    <p className="text-3xl font-bold text-white">{(driverDetails.stats?.average_rating || 5).toFixed(1)}</p>
                  </div>
                  <p className="text-zinc-400 text-sm">Rating</p>
                </div>
              </div>
              
              {/* Delivery History */}
              <div>
                <h5 className="text-lg font-bold text-white mb-3">Histórico de Entregas</h5>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {recentDeliveries.map((delivery) => (
                    <div key={delivery.id || delivery.order?.id} className="bg-zinc-800 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">Pedido #{delivery.order?.id?.slice(0, 8)}</p>
                        <p className="text-zinc-500 text-xs">{formatDate(delivery.delivered_at || delivery.assigned_at)}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={delivery.status === 'delivered' ? 'bg-green-600' : 'bg-yellow-600'}>
                          {delivery.status === 'delivered' ? 'Entregue' : delivery.status}
                        </Badge>
                        {delivery.delivery_fee > 0 && (
                          <p className="text-green-400 text-sm mt-1">+{formatCurrency(delivery.delivery_fee)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!driverDetails.delivery_history || driverDetails.delivery_history.length === 0) && (
                    <p className="text-zinc-500 text-center py-4">Sem histórico de entregas</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
